import React, { useEffect, useRef, useState, useCallback } from 'react';
// import { useSelector, useDispatch } from 'react-redux';
import { useSelector, useDispatch } from 'react-redux';

import moment from 'moment';

import { editNoteFromConversation } from '../../../actions/teams';
import { Textarea } from '@/components/ui/textarea';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import { isMobileViewport, useIsMobile } from '@/hooks/use-mobile';

const userKey = '&$user=';
const dateKey = '&$date=';

// The unsaved new note, kept while the window crosses 768px: phones show the
// notes on the Info screen and wider windows in the side panel, so the notes
// unmount and mount again. In memory only, so a reload starts afresh.
const newNoteDrafts = new Map();

const readNewNoteDraft = (key) => newNoteDrafts.get(key) || '';

const writeNewNoteDraft = (key, value) => {
	if (value) newNoteDrafts.set(key, value);
	else newNoteDrafts.delete(key);
};

// Unsaved edits to saved notes, kept the same way. They come back only while
// the saved notes are still the ones they were made to.
const editedNoteDrafts = new Map();

const readEditedNotes = (key, savedNotes) => {
	const draft = editedNoteDrafts.get(key);
	return draft && JSON.stringify(draft.saved) === JSON.stringify(savedNotes)
		? [...draft.edited]
		: null;
};

const Notes = ({ activeTeamConversation }) => {
	const dispatch = useDispatch();

	const auth = useSelector((state) => state.auth);

	const { user } = auth;

	const draftKey = `${user?._id}:${activeTeamConversation._id}`;

	const [editedNotes, setEditedNotes] = useState(
		() =>
			readEditedNotes(draftKey, activeTeamConversation.notes) || [
				...activeTeamConversation.notes,
			]
	);

	const [initialNotes, setInitialNotes] = useState([
		...activeTeamConversation.notes,
	]);

	const [newNote, setNewNote] = useState(() => readNewNoteDraft(draftKey));

	const [triggerSubmitNewNote, setTriggerSubmitNewNote] = useState(false);

	useEffect(() => {
		writeNewNoteDraft(draftKey, newNote);
	}, [draftKey, newNote]);

	useEffect(() => {
		const hasUnsavedEdits =
			editedNotes.length !== initialNotes.length ||
			editedNotes.some((note, index) => note !== initialNotes[index]);
		if (hasUnsavedEdits) {
			editedNoteDrafts.set(draftKey, { saved: initialNotes, edited: editedNotes });
		} else {
			editedNoteDrafts.delete(draftKey);
		}
	}, [draftKey, editedNotes, initialNotes]);

	// Closing the notes (rather than the layout swapping) drops the drafts.
	const isMobile = useIsMobile();
	const isMobileRef = useRef(isMobile);
	isMobileRef.current = isMobile;
	useEffect(
		() => () => {
			if (isMobileViewport() === isMobileRef.current) {
				writeNewNoteDraft(draftKey, '');
				editedNoteDrafts.delete(draftKey);
			}
		},
		[draftKey]
	);

	// Pick up notes pinned or edited elsewhere, unless this card has unsaved
	// edits, so saving here never drops a note it has not seen
	const notesKey = JSON.stringify(activeTeamConversation.notes || []);

	useEffect(() => {
		const hasUnsavedEdits =
			editedNotes.length !== initialNotes.length ||
			editedNotes.some((note, index) => note !== initialNotes[index]);

		if (!hasUnsavedEdits) {
			setInitialNotes([...(activeTeamConversation.notes || [])]);
			setEditedNotes([...(activeTeamConversation.notes || [])]);
		}
	}, [notesKey]);

	const handleNoteChange = (index, newValue) => {
		const updatedNotes = [...editedNotes];
		updatedNotes[index] = `${newValue}${userKey}${
			user.name || user.username
		}${dateKey}${moment().format('llll')}`;
		setEditedNotes(updatedNotes);
	};

	const hasNoteChanged = (index) => {
		return editedNotes[index] !== initialNotes[index];
	};

	const handleNewNoteChange = (event) => {
		if (event.target.value === '') {
			return setNewNote('');
		}

		// reset triggerSubmitNewNote
		if (triggerSubmitNewNote === true) {
			setTriggerSubmitNewNote(false);
		}

		setNewNote(
			`${event.target.value}${userKey}${
				user.name || user.username
			}${dateKey}${moment().format('llll')}`
		);
	};

	const submitNotes = useCallback(() => {
		// filter out empty notes
		const newNotes = [
			...editedNotes.filter((n) => {
				return n && n.split(userKey) && n.split(userKey)[0];
			}),
		];

		const updatedTeamConversation = {
			...activeTeamConversation,
			notes: newNotes,
		};

		dispatch(
			editNoteFromConversation({
				teamConversation: updatedTeamConversation,
				_business: activeTeamConversation._business,
			})
		);

		setInitialNotes([...newNotes]);
		setEditedNotes([...newNotes]);
	}, [editedNotes]);

	// when new note is set to empty (e.g. the user pushed save) submit the notes
	useEffect(() => {
		if (triggerSubmitNewNote === true) {
			submitNotes();
		}
	}, [triggerSubmitNewNote]);

	const submitNewNote = () => {
		if (newNote) {
			// Implement logic to save the new note or perform the desired action.
			// You may also want to add the new note to the editedNotes array.

			// add new note to the notes array
			setEditedNotes([...editedNotes, newNote]);

			// reset input
			setNewNote('');

			// setSubmitNewNote
			setTriggerSubmitNewNote(true);
		}
	};

	const getNoteValue = (note) => {
		if (note.includes(userKey)) {
			return note.slice(0, note.indexOf(userKey));
		}
		return note;
	};

	const renderUserAndDate = (note) => {
		const name = note.slice(
			note.indexOf(userKey) + userKey.length,
			note.indexOf(dateKey)
		);
		const date = note.slice(note.indexOf(dateKey) + dateKey.length);

		return (
			<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
				<p
					className="text-muted-foreground text-[10.5px]"
					style={{ wordBreak: 'break-word' }}
				>{`${name} - ${date}`}</p>
			</div>
		);
	};

	return (
		<div className="flex flex-col space-y-1.5" key={activeTeamConversation}>
			<div className="space-y-1.5">
				{/* The scroll margin brings Save into view with the field when the keyboard rises over a draft */}
				<Textarea
					placeholder="Add a note…"
					value={getNoteValue(newNote)}
					onChange={handleNewNoteChange}
					className="min-h-[44px] resize-none rounded-xl border-dashed border-border bg-transparent text-[13.5px] leading-5 shadow-none focus-visible:border-solid max-md:scroll-mb-20 max-md:text-base touch:scroll-mb-20"
					id="new"
				/>
				{newNote && (
					<div
						className="space-x-1"
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'flex-start',
								flex: 1,
							}}
						>
							<p
								className="text-muted-foreground text-[10.5px]"
								style={{
									wordBreak: 'break-word',
								}}
							>
								{renderUserAndDate(newNote)}
							</p>
						</div>
						<CustomButton
							size="sm"
							variant="outline"
							onClick={submitNewNote}
						>
							Save
						</CustomButton>
					</div>
				)}
			</div>
			{editedNotes.map((note, index) => {
				const hasUserData = note.includes(userKey);

				const displayVal = getNoteValue(note);

				return (
					<div className="space-y-1" key={index}>
						<Textarea
							value={displayVal}
							onChange={(e) =>
								handleNoteChange(index, e.target.value)
							}
							className="min-h-[40px] resize-none rounded-xl border-amber-200/80 bg-amber-50 text-[13.5px] leading-5 text-foreground shadow-none dark:border-amber-900/60 dark:bg-amber-950/30 max-md:scroll-mb-20 max-md:text-base touch:scroll-mb-20"
						/>
						<div
							style={{
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'space-between',
							}}
						>
							{hasUserData && renderUserAndDate(note)}
							{hasNoteChanged(index) && (
								<CustomButton
									size="sm"
									variant="outline"
									onClick={submitNotes}
								>
									Save
								</CustomButton>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default Notes;
